/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
/* eslint-disable max-len */
/* eslint-disable multiline-ternary */
import React from "react";
import { gql } from "graphql-tag";
import { useQuery } from "@apollo/client";
import {
  BuilderDraft,
  BuilderQuestion,
  buildContentFromDraft,
  emptyDraftQuestion,
  identityToKey,
  parseBuilderDraft,
  readableIdentity,
} from "./form-builder-utils";
import {
  CHILD_LINKED_QUESTIONS_TAB,
  buildQuestionGroupsBySection,
  FormQuestionVariant,
  FormQuestionVariantsResponse,
  getQuestionIdentityKey,
  getQuestionsInTab,
  QUESTIONS_BUILDER_TAB,
  removeArrayChildren,
  UNASSIGNED_QUESTIONS_TAB,
} from "./form-builder-shared";
import { guessQuestionTypeByName } from "./question-type-specs";
import { isValidContextValue } from "./question-identifier-validation";

const STAGED_OVERLAP_ERROR_PREFIX = "Staged question overlaps with existing one:";

const stripStagedOverlapErrors = (errors: string[]): string[] =>
  errors.filter((error) => !error.startsWith(STAGED_OVERLAP_ERROR_PREFIX));

type Params = {
  content: string;
  existingQuestions: Record<string, string>;
  onApplyContent: (content: string) => void | Promise<void>;
};

export type FormBuilderStateValue = {
  draft: BuilderDraft;
  setDraft: React.Dispatch<React.SetStateAction<BuilderDraft>>;
  parseErrors: string[];
  buildErrors: string[];
  buildWarnings: string[];
  selectedBuilderTab: string;
  setSelectedBuilderTab: React.Dispatch<React.SetStateAction<string>>;
  selectedQuestionTab: string;
  setSelectedQuestionTab: React.Dispatch<React.SetStateAction<string>>;
  questionIdentityOptions: string[];
  contextSuggestions: string[];
  questionIdentityLabelMap: Record<string, string>;

  /** Identity key → value type (e.g. select, text) for chips in autocompletes. */
  questionIdentityTypeMap: Record<string, string>;
  questionGroupsBySection: ReturnType<typeof buildQuestionGroupsBySection>;
  questionsInActiveTab: BuilderQuestion[];
  expandedQuestionIds: Set<string>;
  variants: FormQuestionVariant[];
  reloadFromToml: () => void;
  updateQuestion: (
    questionId: string,
    update: (question: BuilderQuestion) => BuilderQuestion,
  ) => void;
  applyVariant: (
    questionId: string,
    variant: FormQuestionVariant | null,
    preserveIdentity?: boolean,
  ) => void;
  applyBuilderToContent: () => Promise<string | null>;
  toggleQuestionExpanded: (questionId: string) => void;
  collapseVisibleQuestions: () => void;
  expandVisibleQuestions: () => void;
  deleteQuestion: (questionId: string, questionKey: string) => void;
  addQuestion: () => void;
};

const GET_FORM_QUESTION_VARIANTS = gql`
  query getFormQuestionVariants {
    formQuestionVariants {
      id
      questionName
      valueType
      version
      title
      usedBy
      rawData
    }
  }
`;

const useFormBuilderState = ({ content, existingQuestions, onApplyContent }: Params): FormBuilderStateValue => {
  const [draft, setDraft] = React.useState(() => parseBuilderDraft(content).draft);
  const [parseErrors, setParseErrors] = React.useState<string[]>([]);
  const [buildErrors, setBuildErrors] = React.useState<string[]>([]);
  const [buildWarnings, setBuildWarnings] = React.useState<string[]>([]);
  const [selectedBuilderTab, setSelectedBuilderTab] = React.useState<string>(QUESTIONS_BUILDER_TAB);
  const [expandedQuestionIds, setExpandedQuestionIds] = React.useState<Set<string>>(new Set());
  const [selectedQuestionTab, setSelectedQuestionTab] = React.useState<string>(UNASSIGNED_QUESTIONS_TAB);

  const { data: variantsResponse } = useQuery<FormQuestionVariantsResponse>(GET_FORM_QUESTION_VARIANTS, {
    fetchPolicy: "no-cache",
  });

  const questionIdentityOptions = React.useMemo(
    () =>
      draft.questions
        .map((question) => getQuestionIdentityKey(question))
        .filter(Boolean),
    [draft.questions],
  );

  const questionIdentityLabelMap = React.useMemo(
    () =>
      questionIdentityOptions.reduce<Record<string, string>>((acc, key) => {
        acc[key] = readableIdentity(key);
        return acc;
      }, {}),
    [questionIdentityOptions],
  );

  const questionIdentityTypeMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    draft.questions.forEach((question) => {
      const key = getQuestionIdentityKey(question);
      const name = question.name.trim();
      map[key] = existingQuestions[name] || guessQuestionTypeByName(name) || "unknown";
    });
    return map;
  }, [draft.questions, existingQuestions]);
  const contextSuggestions = React.useMemo(() => {
    const contextCounts = draft.questions.reduce<Map<string, number>>((acc, question) => {
      question.contextText
        .split(",")
        .map((contextValue) => contextValue.trim())
        .filter(Boolean)
        .forEach((contextValue) => {
          if (contextValue.includes(",")) return;
          if (!isValidContextValue(contextValue)) return;
          acc.set(contextValue, (acc.get(contextValue) || 0) + 1);
        });
      return acc;
    }, new Map<string, number>());

    return Array.from(contextCounts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([contextValue]) => contextValue);
  }, [draft.questions]);

  const questionGroupsBySection = React.useMemo(
    () => buildQuestionGroupsBySection(draft.questions, draft.sections),
    [draft.questions, draft.sections],
  );

  const questionsInActiveTab = React.useMemo(
    () => getQuestionsInTab(questionGroupsBySection, selectedQuestionTab),
    [questionGroupsBySection, selectedQuestionTab],
  );
  const draftRef = React.useRef(draft);
  // Successful apply updates `content`, which re-runs reset; keep merge warnings across that sync.
  const preserveWarningsOnNextResetRef = React.useRef(false);

  React.useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const resetFromToml = React.useCallback(() => {
    const result = parseBuilderDraft(content);
    setDraft(result.draft);
    setParseErrors(result.errors);
    setBuildErrors([]);
    if (preserveWarningsOnNextResetRef.current) {
      preserveWarningsOnNextResetRef.current = false;
    } else {
      setBuildWarnings([]);
    }
    setSelectedBuilderTab(QUESTIONS_BUILDER_TAB);
    setExpandedQuestionIds(new Set());
    setSelectedQuestionTab(UNASSIGNED_QUESTIONS_TAB);
  }, [content]);

  React.useEffect(() => {
    resetFromToml();
  }, [resetFromToml]);

  React.useEffect(() => {
    if (
      selectedQuestionTab === UNASSIGNED_QUESTIONS_TAB ||
      selectedQuestionTab === CHILD_LINKED_QUESTIONS_TAB
    ) {
      return;
    }

    const tabStillExists = draft.sections.some((section) => section.draftId === selectedQuestionTab);
    if (!tabStillExists) setSelectedQuestionTab(UNASSIGNED_QUESTIONS_TAB);
  }, [draft.sections, selectedQuestionTab]);

  const updateQuestion = React.useCallback(
    (questionId: string, update: (question: BuilderQuestion) => BuilderQuestion) => {
      const previous = draftRef.current;
      const previousQuestion = previous.questions.find((question) => question.draftId === questionId);
      if (!previousQuestion) return;

      const nextQuestion = update(previousQuestion);
      const previousKey = getQuestionIdentityKey(previousQuestion);
      const nextKey = getQuestionIdentityKey(nextQuestion);
      const identityChanged = previousKey !== nextKey;

      if (identityChanged) {
        const hasOverlap = previous.questions.some(
          (question) => question.draftId !== questionId && getQuestionIdentityKey(question) === nextKey,
        );

        if (hasOverlap) {
          const overlapError = `${STAGED_OVERLAP_ERROR_PREFIX} ${readableIdentity(nextKey)}.`;
          setBuildErrors((errors) => [...stripStagedOverlapErrors(errors), overlapError]);
          return;
        }
      }

      const nextDraft: BuilderDraft = {
        ...previous,
        questions: previous.questions.map((question) => (
          question.draftId === questionId ? nextQuestion : question
        )),
      };

      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setBuildErrors((errors) => stripStagedOverlapErrors(errors));
    },
    [],
  );

  const applyVariant = React.useCallback(
    (
      questionId: string,
      variant: FormQuestionVariant | null,
      preserveIdentity = false,
    ) => {
      if (!variant) return;

      updateQuestion(questionId, (question) => {
        try {
          const parsedVariant = JSON.parse(variant.rawData);
          const typedVariant = parsedVariant as {
            title?: string;
            required?: boolean;
            alias?: string;
            cond?: { type?: string; values?: string[] };
            children?: string[][];
            data?: Record<string, unknown>;
            type?: string;
          };

          const variantType = typedVariant.type || variant.valueType;
          const dataObject = typedVariant.data || {};
          const children =
            variantType === "array"
              ? ((dataObject.children as string[][]) || [])
              : (typedVariant.children || []);

          return {
            ...question,
            name: preserveIdentity ? question.name : variant.questionName,
            title: typedVariant.title || variant.title || "",
            required: Boolean(typedVariant.required),
            alias: typedVariant.alias || "",
            condValuesText: typedVariant.cond?.type === "includes"
              ? (typedVariant.cond?.values || []).join(", ")
              : "",
            childrenKeys: children.map(identityToKey),
            dataJson: JSON.stringify(
              variantType === "array" ? removeArrayChildren(dataObject) : dataObject,
              null,
              2,
            ),
          };
        } catch (error) {
          setBuildErrors([`Unable to load variant ${variant.id}: ${String(error)}`]);
          return question;
        }
      });
    },
    [updateQuestion],
  );

  const applyBuilderToContent = React.useCallback(async (): Promise<string | null> => {
    const result = buildContentFromDraft(draft, existingQuestions);
    setBuildErrors(result.errors);
    setBuildWarnings(result.warnings);

    if (result.errors.length === 0) {
      preserveWarningsOnNextResetRef.current = true;
      await Promise.resolve(onApplyContent(result.content));
      // If content was unchanged, reset never consumed the flag — drop it next macrotask.
      setTimeout(() => {
        preserveWarningsOnNextResetRef.current = false;
      }, 0);
      return result.content;
    }
    return null;
  }, [draft, existingQuestions, onApplyContent]);

  const toggleQuestionExpanded = React.useCallback((questionId: string) => {
    setExpandedQuestionIds((previous) => {
      const next = new Set(previous);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  }, []);

  const collapseVisibleQuestions = React.useCallback(() => {
    setExpandedQuestionIds((previous) => {
      const next = new Set(previous);
      questionsInActiveTab.forEach((question) => next.delete(question.draftId));
      return next;
    });
  }, [questionsInActiveTab]);

  const expandVisibleQuestions = React.useCallback(() => {
    setExpandedQuestionIds((previous) => {
      const next = new Set(previous);
      questionsInActiveTab.forEach((question) => next.add(question.draftId));
      return next;
    });
  }, [questionsInActiveTab]);

  const deleteQuestion = React.useCallback((questionId: string, questionKey: string) => {
    setDraft((previous) => ({
      ...previous,
      questions: previous.questions.filter((entry) => entry.draftId !== questionId),
      sections: previous.sections.map((section) => ({
        ...section,
        questionKeys: section.questionKeys.filter((entry) => entry !== questionKey),
      })),
    }));

    setExpandedQuestionIds((previous) => {
      const next = new Set(previous);
      next.delete(questionId);
      return next;
    });
  }, []);

  const addQuestion = React.useCallback(() => {
    const newQuestion = emptyDraftQuestion();
    setDraft((previous) => ({
      ...previous,
      questions: [...previous.questions, newQuestion],
    }));
    setExpandedQuestionIds((previous) => {
      const next = new Set(previous);
      next.add(newQuestion.draftId);
      return next;
    });
  }, []);

  return {
    draft,
    setDraft,
    parseErrors,
    buildErrors,
    buildWarnings,
    selectedBuilderTab,
    setSelectedBuilderTab,
    selectedQuestionTab,
    setSelectedQuestionTab,
    questionIdentityOptions,
    contextSuggestions,
    questionIdentityLabelMap,
    questionIdentityTypeMap,
    questionGroupsBySection,
    questionsInActiveTab,
    expandedQuestionIds,
    variants: variantsResponse?.formQuestionVariants || [],
    reloadFromToml: resetFromToml,
    updateQuestion,
    applyVariant,
    applyBuilderToContent,
    toggleQuestionExpanded,
    collapseVisibleQuestions,
    expandVisibleQuestions,
    deleteQuestion,
    addQuestion,
  };
};

export default useFormBuilderState;
